import { Link } from "react-router-dom";
import { Mail } from "lucide-react";

const PRODUCT_LINKS = [
  { label: "Features", href: "/#features", testId: "footer-link-features" },
  { label: "About", to: "/about", testId: "footer-link-about" },
  { label: "Support", to: "/support", testId: "footer-link-support" },
];

const LEGAL_LINKS = [
  { label: "Privacy Policy", to: "/privacy", testId: "footer-privacy-link" },
  { label: "Terms of Use", to: "/terms", testId: "footer-terms-link" },
  { label: "Safety Notice", to: "/safety", testId: "footer-safety-link" },
];

const FooterLink = ({ link }) => {
  const className = "text-sm text-slate-400 hover:text-white transition-colors w-fit";

  if (link.to) {
    return (
      <Link to={link.to} data-testid={link.testId} className={className}>
        {link.label}
      </Link>
    );
  }

  return (
    <a href={link.href} data-testid={link.testId} className={className}>
      {link.label}
    </a>
  );
};

const FooterSection = ({ title, links }) => (
  <div className="flex flex-col gap-4">
    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
      {title}
    </span>
    <div className="flex flex-col gap-3">
      {links.map((link) => (
        <FooterLink key={link.label} link={link} />
      ))}
    </div>
  </div>
);

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-white/[0.06] bg-[#060910]"
    >
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.4fr_0.8fr_0.9fr_1fr]">
          {/* Brand */}
          <div className="flex flex-col gap-4 max-w-sm">
            <div className="flex items-center gap-2">
              <img
                src="/branding/MockTP_Logo_Transparent.svg"
                alt="MockTP"
                className="h-14 w-14 object-contain shrink-0"
              />
              <span className="text-[2.05rem] font-bold leading-none tracking-tight text-white">
                Mock<span className="text-[#00DC82]">TP</span>
              </span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              Built by instructors, for instructors.
            </p>
            <p className="text-xs text-slate-500">© 2026 MockTP Labs</p>
          </div>

          <FooterSection title="Product" links={PRODUCT_LINKS} />
          <FooterSection title="Legal" links={LEGAL_LINKS} />

          {/* Contact */}
          <div className="flex flex-col gap-4">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Contact
            </span>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
                  Email
                </span>
                <a
                  href="mailto:support@mocktp.app"
                  aria-label="Email MockTP"
                  data-testid="footer-email-link"
                  className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#00DC82] transition-colors w-fit"
                >
                  <Mail size={16} />
                  support@mocktp.app
                </a>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
                  Website
                </span>
                <a
                  href="https://mocktp.app"
                  data-testid="footer-website-link"
                  className="text-sm text-slate-400 hover:text-[#00DC82] transition-colors w-fit"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  mocktp.app
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
